import {AdminLayout} from './components/layouts/AdminLayout';
import {AppLayout} from './components/layouts/AppLayout';
import {AuthLayout} from './components/layouts/AuthLayout';
import {DashboardLayout} from './components/layouts/DashboardLayout';
import {Navigate, createBrowserRouter} from 'react-router-dom';
import {RedirectIfAuth} from './components/guards/RedirectIfAuth';
import {RequireAdmin} from './components/guards/RequireAdmin';
import {RequireAuth} from './components/guards/RequireAuth';
import {RequireOwner} from './components/guards/RequireOwner';

import {AcceptInvitePage} from './pages/team/AcceptInvitePage';
import {AccountDeletionsPage} from './pages/superadmin/AccountDeletionsPage';
import {AdminDashboardPage} from './pages/admin/AdminDashboardPage';
import {AdminSitesPage} from './pages/admin/AdminSitesPage';
import {AdminUsersPage} from './pages/admin/AdminUsersPage';
import {AllInvoicesPage} from './pages/billing/AllInvoicesPage';
import {ApiTokensPage} from './pages/settings/ApiTokensPage';
import {BillingPage} from './pages/billing/BillingPage';
import {CreateSitePage} from './pages/sites/CreateSitePage';
import {DashboardPage} from './pages/dashboard/DashboardPage';
import {DeleteAccountPage} from './pages/settings/DeleteAccountPage';
import {DfyOrdersPage} from './pages/admin/DfyOrdersPage';
import {EditTeamMemberPage} from './pages/team/EditTeamMemberPage';
import {ForgotPasswordPage} from './pages/auth/ForgotPasswordPage';
import {InviteTeamMemberPage} from './pages/team/InviteTeamMemberPage';
import {LoginPage} from './pages/auth/LoginPage';
import {LogoutPage} from './pages/auth/LogoutPage';
import {MarketplaceAdminPage} from './pages/admin/MarketplaceAdminPage';
import {OpenSitePage} from './pages/sites/OpenSitePage';
import {PlanEditorPage} from './pages/admin/plans/PlanEditorPage';
import {PlansListPage} from './pages/admin/plans/PlansListPage';
import {PricingPage} from './pages/pricing/PricingPage';
import {ProvisioningPage} from './pages/sites/ProvisioningPage';
import {PublicationSettingsPage} from './pages/sites/PublicationSettingsPage';
import {TeamPage} from './pages/team/TeamPage';
import {ThemeBuilderPage} from './pages/themes/ThemeBuilderPage';
import {ThemeStorePage} from './pages/themes/ThemeStorePage';
import {UpgradePage} from './pages/billing/UpgradePage';

export const routes = [
    {
        path: '/',
        element: <Navigate to="/dashboard" replace />
    },

    {
        path: '/team/accept',
        element: <AcceptInvitePage />
    },

    {
        path: '/pricing',
        element: <PricingPage />
    },

    {
        path: '/logout',
        element: <LogoutPage />
    },

    {
        element: (
            <RedirectIfAuth>
                <AuthLayout />
            </RedirectIfAuth>
        ),
        children: [
            {
                path: '/login',
                element: <LoginPage />
            },
            {
                path: '/forgot-password',
                element: <ForgotPasswordPage />
            }
        ]
    },

    {
        element: (
            <RequireAuth>
                <AppLayout />
            </RequireAuth>
        ),
        children: [
            {path: '/sites/:id/open', element: <OpenSitePage />},
            {
                element: <DashboardLayout />,
                children: [
                    {path: '/settings/delete-account', element: <DeleteAccountPage />},
                    {path: '/sites/new', element: <CreateSitePage />},
                    {path: '/sites/:id/provisioning', element: <ProvisioningPage />},
                    {path: '/sites/:id/settings', element: <PublicationSettingsPage />},
                    {path: '/team/invite', element: <InviteTeamMemberPage />},
                    {
                        path: '/dashboard',
                        element: <DashboardPage />
                    },
                    {
                        path: '/team',
                        element: <TeamPage />
                    },
                    {
                        path: '/team/:memberId',
                        element: <EditTeamMemberPage />
                    },
                    {
                        path: '/billing',
                        element: <BillingPage />
                    },
                    {
                        path: '/billing/invoices',
                        element: <AllInvoicesPage />
                    },
                    {
                        path: '/billing/upgrade',
                        element: <UpgradePage />
                    },
                    {
                        path: '/themes',
                        element: <RequireOwner><ThemeStorePage /></RequireOwner>
                    },
                    {
                        path: '/themes/builder',
                        element: <RequireOwner><ThemeBuilderPage /></RequireOwner>
                    },
                    {
                        path: '/themes/builder/new',
                        element: <RequireOwner><ThemeBuilderPage /></RequireOwner>
                    },
                    {
                        path: '/themes/builder/projects/:projectId',
                        element: <RequireOwner><ThemeBuilderPage /></RequireOwner>
                    },
                    {
                        path: '/themes/builder/:siteId',
                        element: <Navigate to="/themes/builder" replace />
                    },
                    {
                        path: '/settings/api-tokens',
                        element: <ApiTokensPage />
                    }
                ]
            },

            {
                element: (
                    <RequireAdmin>
                        <AdminLayout />
                    </RequireAdmin>
                ),
                children: [
                    {
                        path: '/superadmin/account-deletions',
                        element: <AccountDeletionsPage />
                    },
                    {
                        path: '/superadmin',
                        element: <AdminDashboardPage />
                    },
                    {
                        path: '/superadmin/users',
                        element: <AdminUsersPage />
                    },
                    {
                        path: '/superadmin/sites',
                        element: <AdminSitesPage />
                    },
                    {
                        path: '/superadmin/plans',
                        element: <PlansListPage />
                    },
                    {
                        path: '/superadmin/plans/new',
                        element: <PlanEditorPage />
                    },
                    {
                        path: '/superadmin/plans/:id',
                        element: <PlanEditorPage />
                    },
                    {
                        path: '/superadmin/marketplace',
                        element: <MarketplaceAdminPage />
                    },
                    {
                        path: '/superadmin/dfy-orders',
                        element: <DfyOrdersPage />
                    }
                ]
            }
        ]
    },

    {
        path: '*',
        element: <Navigate to="/dashboard" replace />
    }
];

export const router = createBrowserRouter(routes);
